import { Formik, type FormikHelpers, type FormikProps } from 'formik';
import { useEffect, useMemo, useRef, type FormEvent } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Button, Form } from 'react-bootstrap';
import Select, { type SingleValue } from 'react-select';
import * as yup from 'yup';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import {
  useLazyGetProviderKeyQuery,
  useLazyGetProviderModelsQuery,
  useUpdateProviderKeyMutation
} from '@/app/Settings/api/aiSettingsApi';
import type {
  ModelOption,
  ProviderKeyDetail
} from '@/app/Settings/types/aiSettingsApiTypes';
import { errorMessage, successMessage } from '@/utils/toast';

type EditApiKeyFormValues = {
  key_name: string;
  provider: string;
  provider_id: string;
  model: string;
  api_key: string;
  is_active: boolean;
};

type EditAIApiKeyModalProps = {
  show: boolean;
  keyId?: string;
  onHide: () => void;
  onSubmit?: (updated: ProviderKeyDetail) => void;
};

type ApiErrorShape = {
  data?: { msg?: string };
};

const editApiKeySchema = yup.object().shape({
  model: yup.string().required('مدل الزامی است'),
  is_active: yup.boolean().required()
});

const EditAIApiKeyModal = ({
  show,
  keyId,
  onHide,
  onSubmit
}: EditAIApiKeyModalProps) => {
  const formikRef = useRef<FormikProps<EditApiKeyFormValues>>(null);
  const [getProviderKey, { data: keyDetail, isFetching: isFetchingKey }] =
    useLazyGetProviderKeyQuery();
  const [getProviderModels, { data: modelsData, isFetching: isFetchingModels }] =
    useLazyGetProviderModelsQuery();
  const [updateProviderKey, { isLoading: isUpdating }] =
    useUpdateProviderKeyMutation();

  const models = useMemo(
    () => (Array.isArray(modelsData) ? modelsData : []),
    [modelsData]
  );

  useEffect(() => {
    if (!show || !keyId) return;
    getProviderKey({ id: keyId });
  }, [show, keyId, getProviderKey]);

  useEffect(() => {
    if (!show || !keyDetail?.provider_id) return;
    getProviderModels(keyDetail.provider_id);
  }, [show, keyDetail?.provider_id, getProviderModels]);

  const initialValues = useMemo<EditApiKeyFormValues>(
    () => ({
      key_name: keyDetail?.key_name ?? '',
      provider: keyDetail?.provider ?? '',
      provider_id: keyDetail?.provider_id ?? '',
      model: keyDetail?.model_id ?? '',
      api_key: keyDetail?.encrypted_api_key ?? '',
      is_active: Boolean(keyDetail?.is_active)
    }),
    [keyDetail]
  );

  const handleFormSubmit = async (
    values: EditApiKeyFormValues,
    actions: FormikHelpers<EditApiKeyFormValues>
  ) => {
    if (!keyId) return;
    try {
      const updated = await updateProviderKey({
        id: keyId,
        model_id: values.model,
        is_active: values.is_active
      }).unwrap();
      successMessage('API Key با موفقیت ویرایش شد');
      onSubmit?.(updated);
    } catch (err) {
      const error = err as ApiErrorShape;
      errorMessage(error?.data?.msg || 'خطا در ویرایش API Key');
    } finally {
      actions.setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Formik<EditApiKeyFormValues>
        innerRef={formikRef}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={editApiKeySchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting: isFormikSubmitting
        }) => (
          <Form
            autoComplete="off"
            onSubmit={(e: FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Modal.Header closeButton>
              <Modal.Title className="fs-1">ویرایش API Key</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {isFetchingKey ? (
                <div className="d-flex flex-column gap-2">
                  <TextLoading height={38} width="100%" />
                  <TextLoading height={38} width="100%" />
                  <TextLoading height={38} width="100%" />
                </div>
              ) : (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>نام کلید</Form.Label>
                    <Form.Control
                      type="text"
                      value={values.key_name || '-'}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>ارائه دهنده</Form.Label>
                    <Form.Control
                      type="text"
                      value={values.provider || '-'}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>API Key</Form.Label>
                    <Form.Control
                      type="text"
                      value={values.api_key || '-'}
                      disabled
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>مدل</Form.Label>
                    {isFetchingModels ? (
                      <div style={{ margin: '4px 0px' }}>
                        <TextLoading height={38} width="100%" />
                      </div>
                    ) : (
                      <Select
                        name="ai_edit_model_select"
                        inputId="ai-edit-model-select"
                        menuShouldScrollIntoView
                        closeMenuOnSelect
                        isSearchable={false}
                        options={models}
                        classNamePrefix="react-select"
                        placeholder="انتخاب مدل"
                        value={
                          models.find(model => model.value === values.model) ??
                          null
                        }
                        onChange={(
                          selectedOption: SingleValue<ModelOption>
                        ) => {
                          setFieldValue('model', selectedOption?.value ?? '');
                        }}
                        onBlur={() => setFieldTouched('model', true)}
                        isDisabled={models.length === 0}
                        maxMenuHeight={300}
                        noOptionsMessage={() => 'مدلی یافت نشد'}
                      />
                    )}
                    {touched.model && errors.model ? (
                      <div className="invalid-feedback d-block">
                        {errors.model}
                      </div>
                    ) : null}
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Check
                      type="switch"
                      id="edit-api-key-active"
                      className="mb-0"
                      checked={Boolean(values.is_active)}
                      onChange={e =>
                        setFieldValue('is_active', e.target.checked)
                      }
                      label={values.is_active ? 'فعال' : 'غیرفعال'}
                    />
                  </Form.Group>
                </>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={onHide}>
                انصراف
              </Button>
              <Button
                type="submit"
                variant="falcon-primary"
                disabled={
                  isFetchingKey ||
                  isFetchingModels ||
                  isFormikSubmitting ||
                  isUpdating
                }
              >
                ویرایش
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditAIApiKeyModal;
